import React from 'react';
import copy from 'clipboard-copy';
import { useTranslation } from 'common/i18n/hooks';
import { useQueryFeatureFlags } from 'iron/apps/experiments/src/components/hooks/query-feature-flag-list';
import { useStagedExperiment } from 'iron/apps/experiments/src/components/hooks/staged-experiment';
import { getReportUrl } from 'common/report/util';
import { addToast } from 'common/components/toast-area/toast-manager';
import { ToastType } from 'common/components/toast/types';
import { SvgIconReact } from 'common/components/svg-icon';
import { useExperimentPageContext } from 'iron/apps/experiments/src/components/context';
import { TagInputSearchMode } from 'common/components/tag-input/types';
import { InputSize } from 'common/components/input/types';
import { TagInputReact } from 'common/components/tag-input';
import { ExperimentPageConfigurationSetting } from '../_setting';
import parentStyles from '../index.module.styl';
import styles from './feature-flag.module.styl';
export function CollectionMethodFeatureFlag(_ref: {
  isEditing: boolean;
}) {
  let {
    isEditing
  }: {
    isEditing: boolean;
  } = _ref;
  return isEditing ? <EditableFeatureFlags data-sentry-element="EditableFeatureFlags" data-sentry-component="CollectionMethodFeatureFlag" data-sentry-source-file="feature-flag.tsx" /> : <ReadonlyFeatureFlags data-sentry-element="ReadonlyFeatureFlags" data-sentry-component="CollectionMethodFeatureFlag" data-sentry-source-file="feature-flag.tsx" />;
}
function EditableFeatureFlags() {
  return <>
      <EditableFeatureFlagId data-sentry-element="EditableFeatureFlagId" data-sentry-source-file="feature-flag.tsx" />
      <EditableFeatureFlagControl data-sentry-element="EditableFeatureFlagControl" data-sentry-source-file="feature-flag.tsx" />
    </>;
}
function EditableFeatureFlagId() {
  var _stagedExperiment$set, _stagedExperiment$set3;
  const i18n = useTranslation();
  const {
    stagedExperiment,
    stageChanges
  } = useStagedExperiment();
  const {
    featureFlags,
    stagedFeatureFlag,
    variants,
    defaultVariant
  } = useQueryFeatureFlags();
  const defaultApplied = React.useRef(true);
  if (!defaultApplied.current && stagedExperiment !== null && stagedExperiment !== void 0 && (_stagedExperiment$set = stagedExperiment.settings) !== null && _stagedExperiment$set !== void 0 && (_stagedExperiment$set = _stagedExperiment$set.featureFlagSettings) !== null && _stagedExperiment$set !== void 0 && _stagedExperiment$set.featureFlagId && variants !== null && variants !== void 0 && variants.length) {
    defaultApplied.current = true;
    stageChanges({
      settings: {
        controlKey: defaultVariant === null || defaultVariant === void 0 ? void 0 : defaultVariant.key
      }
    });
  }
  const handleEditFeatureFlagId = (value: string) => {
    defaultApplied.current = false;
    stageChanges({
      settings: {
        featureFlagSettings: {
          featureFlagId: value
        },
        controlKey: ""
      }
    });
  };
  if (!stagedExperiment) {
    return null;
  }
  return <ExperimentPageConfigurationSetting label={i18n.t("experiments.configuration.feature-flag.feature-flag-id")} data-sentry-element="ExperimentPageConfigurationSetting" data-sentry-component="EditableFeatureFlagId" data-sentry-source-file="feature-flag.tsx">
      <TagInputReact className={parentStyles.fullWidth} menuSections={[{
      items: featureFlags.map(featureFlag => {
        var _stagedExperiment$set2;
        return {
          label: featureFlag.name,
          value: featureFlag.id,
          isSelected: featureFlag.id === (stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set2 = stagedExperiment.settings) === null || _stagedExperiment$set2 === void 0 || (_stagedExperiment$set2 = _stagedExperiment$set2.featureFlagSettings) === null || _stagedExperiment$set2 === void 0 ? void 0 : _stagedExperiment$set2.featureFlagId),
          subLabel: featureFlag.description || undefined
        };
      })
    }]} controlledMode size={InputSize.Small} searchMode={TagInputSearchMode.Text} tags={stagedFeatureFlag ? [{
      label: stagedFeatureFlag.name,
      value: stagedFeatureFlag.id
    }] : []} icon={stagedExperiment !== null && stagedExperiment !== void 0 && (_stagedExperiment$set3 = stagedExperiment.settings) !== null && _stagedExperiment$set3 !== void 0 && (_stagedExperiment$set3 = _stagedExperiment$set3.featureFlagSettings) !== null && _stagedExperiment$set3 !== void 0 && _stagedExperiment$set3.featureFlagId ? "checkmark" : "search"} mpItemsMenuAttrs={{
      'empty-message': i18n.t("experiments.configuration.no-feature-flag-results"),
      'empty-search-results-message': i18n.t("experiments.configuration.no-feature-flag-results")
    }} menuWidth={-1} searchWidth={100} onChange={() => {
      handleEditFeatureFlagId("");
    }} onClickMenuItem={(ev: TODO) => {
      handleEditFeatureFlagId(ev.detail.value);
    }} data-sentry-element="TagInputReact" data-sentry-source-file="feature-flag.tsx" />
    </ExperimentPageConfigurationSetting>;
}
function EditableFeatureFlagControl() {
  var _stagedExperiment$set4, _stagedExperiment$set5, _stagedExperiment$set7;
  const i18n = useTranslation();
  const {
    stagedExperiment,
    stageChanges
  } = useStagedExperiment();
  const {
    variants
  } = useQueryFeatureFlags();
  const handleEditControlKey = (value: string) => {
    stageChanges({
      settings: {
        controlKey: value
      }
    });
  };
  if (!(stagedExperiment !== null && stagedExperiment !== void 0 && (_stagedExperiment$set4 = stagedExperiment.settings) !== null && _stagedExperiment$set4 !== void 0 && (_stagedExperiment$set4 = _stagedExperiment$set4.featureFlagSettings) !== null && _stagedExperiment$set4 !== void 0 && _stagedExperiment$set4.featureFlagId)) {
    return null;
  }
  const draftControlKey = stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set5 = stagedExperiment.settings) === null || _stagedExperiment$set5 === void 0 ? void 0 : _stagedExperiment$set5.controlKey;
  return <ExperimentPageConfigurationSetting label={i18n.t("experiments.configuration.feature-flag.control-key")} helpText={i18n.t("experiments.configuration.feature-flag.control-key-info")} data-sentry-element="ExperimentPageConfigurationSetting" data-sentry-component="EditableFeatureFlagControl" data-sentry-source-file="feature-flag.tsx">
      <TagInputReact className={parentStyles.fullWidth} menuSections={[{
      items: variants.map(variant => {
        var _stagedExperiment$set6;
        return {
          label: variant.key,
          value: variant.key,
          isSelected: variant.key === (stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set6 = stagedExperiment.settings) === null || _stagedExperiment$set6 === void 0 ? void 0 : _stagedExperiment$set6.controlKey),
          subLabel: variant.description || undefined
        };
      })
    }]} controlledMode size={InputSize.Small} searchMode={TagInputSearchMode.Text} icon={stagedExperiment !== null && stagedExperiment !== void 0 && (_stagedExperiment$set7 = stagedExperiment.settings) !== null && _stagedExperiment$set7 !== void 0 && _stagedExperiment$set7.controlKey ? "checkmark" : "search"} tags={draftControlKey ? [{
      label: draftControlKey,
      value: draftControlKey
    }] : []} mpItemsMenuAttrs={{
      'empty-message': i18n.t("experiments.configuration.no-feature-flag-variant-results"),
      'empty-search-results-message': i18n.t("experiments.configuration.no-feature-flag-variant-results")
    }} menuWidth={-1} searchWidth={100} onChange={() => {
      handleEditControlKey("");
    }} onClickMenuItem={(ev: TODO) => {
      handleEditControlKey(ev.detail.value);
    }} data-sentry-element="TagInputReact" data-sentry-source-file="feature-flag.tsx" />
    </ExperimentPageConfigurationSetting>;
}
function ReadonlyFeatureFlags() {
  const i18n = useTranslation();
  const {
    projectId,
    workspaceId
  } = useExperimentPageContext();
  const {
    stagedExperiment
  } = useStagedExperiment();
  const {
    stagedFeatureFlag
  } = useQueryFeatureFlags();
  const onNavigateToFeatureFlag = () => {
    if (stagedFeatureFlag !== null && stagedFeatureFlag !== void 0 && stagedFeatureFlag.key) {
      const reportUrl = getReportUrl("feature-flags/".concat(stagedFeatureFlag === null || stagedFeatureFlag === void 0 ? void 0 : stagedFeatureFlag.id), {
        projectId,
        workspaceId
      });
      window.open(reportUrl, "_blank");
    }
  };
  const onCopyFeatureFlagKey = async () => {
    if (stagedFeatureFlag !== null && stagedFeatureFlag !== void 0 && stagedFeatureFlag.key) {
      await copy(stagedFeatureFlag.key);
      addToast({
        type: ToastType.SUCCESS,
        message: i18n.t("components.copy-to-clipboard.copied")
      });
    }
  };
  if (!stagedExperiment) {
    return null;
  }
  return <>
      <ExperimentPageConfigurationSetting label={i18n.t("experiments.configuration.feature-flag.feature-flag-id")} data-sentry-element="ExperimentPageConfigurationSetting" data-sentry-source-file="feature-flag.tsx">
        {(stagedFeatureFlag === null || stagedFeatureFlag === void 0 ? void 0 : stagedFeatureFlag.name) && <div className={styles.actionLabel}>
            <span className={styles.label} title={stagedFeatureFlag === null || stagedFeatureFlag === void 0 ? void 0 : stagedFeatureFlag.name}>
              {stagedFeatureFlag === null || stagedFeatureFlag === void 0 ? void 0 : stagedFeatureFlag.name}
            </span>
            <SvgIconReact className={styles.actionBtn} icon='externalLink' onClick={onNavigateToFeatureFlag} />
          </div>}
      </ExperimentPageConfigurationSetting>
      <ExperimentPageConfigurationSetting label={i18n.t("experiments.configuration.feature-flag.feature-flag-key")} data-sentry-element="ExperimentPageConfigurationSetting" data-sentry-source-file="feature-flag.tsx">
        {(stagedFeatureFlag === null || stagedFeatureFlag === void 0 ? void 0 : stagedFeatureFlag.key) && <div className={styles.actionLabel}>
            <span className={styles.label} title={stagedFeatureFlag === null || stagedFeatureFlag === void 0 ? void 0 : stagedFeatureFlag.key}>
              {stagedFeatureFlag === null || stagedFeatureFlag === void 0 ? void 0 : stagedFeatureFlag.key}
            </span>
            <SvgIconReact className={styles.actionBtn} icon='copy' onClick={onCopyFeatureFlagKey} />
          </div>}
      </ExperimentPageConfigurationSetting>
    </>;
}